import React from "react";
import FormField, { selectBaseStyle } from "../../../shared/ui/FormField";
import Input from "../../../shared/ui/Input";
import ProfileHealthIssuesField from "./ProfileHealthIssuesField";

function formatDateBR(value) {
  if (!value) return "";

  const normalized = String(value).trim();
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch {
    return value;
  }
}

function SelectChevron() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 9L12 15L18 9"
        stroke="#6C54D8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ProfileGoalsSection({
  isEditing,
  profile,
  setProfile,
  healthQuery,
  setHealthQuery,
  isHealthOpen,
  setIsHealthOpen,
  healthInputRef,
  healthDropdownWrapperRef,
  healthOptions,
  canAddMoreHealthIssues,
  addHealthIssue,
  removeHealthIssue,
  handleHealthKeyDown,
}) {
  const currentObjective = profile.diagnosis || "Emagrecimento";

  return (
    <section className="space-y-4 pt-1">
      {/* SELETOR DE OBJETIVO PRINCIPAL */}
      <div className="liti-card p-5 space-y-3 border-l-4 border-l-[#6C54D8]">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#6C54D8]" />
          <h2 className="text-sm font-bold text-[#24152F] tracking-tight">
            Objetivo Principal
          </h2>
        </div>

        <FormField
          label="Selecione o seu foco de acompanhamento"
          isEditing={isEditing}
          readOnlyValue={currentObjective}
        >
          <div className="relative">
            <select
              style={selectBaseStyle}
              value={currentObjective}
              onChange={(event) =>
                setProfile((prev) => ({
                  ...prev,
                  diagnosis: event.target.value,
                }))
              }
            >
              <option value="Emagrecimento">🌱 Emagrecimento e Composição Corporal</option>
              <option value="Saúde e bem-estar">🩺 Saúde e Bem-Estar Geral</option>
              <option value="Performance">⚡ Performance e Condicionamento Físico</option>
            </select>

            <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
              <SelectChevron />
            </div>
          </div>
        </FormField>
      </div>

      {/* METAS PERSONALIZADAS: EMAGRECIMENTO */}
      {(currentObjective === "Emagrecimento" ||
        currentObjective === "Emagrecimento e Composição Corporal") && (
        <div className="liti-card p-5 space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚖️</span>
            <h3 className="text-sm font-bold text-[#24152F]">
              Metas de Emagrecimento
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Peso desejado (kg)"
              isEditing={isEditing}
              readOnlyValue={profile.goalWeight}
              readOnlySuffix={profile.goalWeight ? " kg" : ""}
            >
              <Input
                type="number"
                placeholder="Ex: 62"
                value={profile.goalWeight}
                onChange={(event) =>
                  setProfile((prev) => ({
                    ...prev,
                    goalWeight: event.target.value,
                  }))
                }
              />
            </FormField>

            <FormField
              label="Data limite para a meta"
              isEditing={isEditing}
              readOnlyValue={formatDateBR(profile.goalDate)}
            >
              <Input
                type="date"
                value={profile.goalDate}
                onChange={(event) =>
                  setProfile((prev) => ({
                    ...prev,
                    goalDate: event.target.value,
                  }))
                }
              />
            </FormField>
          </div>

          <div className="rounded-2xl bg-[#ECE7FA]/50 p-3.5 border border-[#ECE7FA] text-xs space-y-1 text-[#4B367C]">
            <p className="font-bold">🎯 Foco do seu programa:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs text-[#6D6680] font-medium">
              <li>Perda de gordura gradual e sustentável com preservação de massa magra.</li>
              <li>Acompanhamento de peso, dosagem de medicações e hábitos diários.</li>
            </ul>
          </div>
        </div>
      )}

      {/* METAS PERSONALIZADAS: SAÚDE E BEM-ESTAR */}
      {(currentObjective === "Saúde e bem-estar" ||
        currentObjective === "Saúde" ||
        currentObjective === "Saúde e Bem-Estar Geral") && (
        <div className="liti-card p-5 space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <span className="text-lg">🩺</span>
            <h3 className="text-sm font-bold text-[#24152F]">
              Metas de Saúde e Prevenção
            </h3>
          </div>

          <div className="rounded-2xl bg-[#ECE7FA]/50 p-3.5 border border-[#ECE7FA] text-xs space-y-1 text-[#4B367C]">
            <p className="font-bold">✨ Foco do seu programa:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs text-[#6D6680] font-medium">
              <li>Melhora dos marcadores laboratoriais, longevidade e disposição diária.</li>
              <li>Consistência na rotina de sono, hidratação e bem-estar integral.</li>
            </ul>
          </div>

          <ProfileHealthIssuesField
            isEditing={isEditing}
            profile={profile}
            healthQuery={healthQuery}
            setHealthQuery={setHealthQuery}
            isHealthOpen={isHealthOpen}
            setIsHealthOpen={setIsHealthOpen}
            healthInputRef={healthInputRef}
            healthDropdownWrapperRef={healthDropdownWrapperRef}
            healthOptions={healthOptions}
            canAddMoreHealthIssues={canAddMoreHealthIssues}
            addHealthIssue={addHealthIssue}
            removeHealthIssue={removeHealthIssue}
            handleHealthKeyDown={handleHealthKeyDown}
          />
        </div>
      )}

      {/* METAS PERSONALIZADAS: PERFORMANCE */}
      {(currentObjective === "Performance" ||
        currentObjective === "Performance e Condicionamento Físico") && (
        <div className="liti-card p-5 space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚡</span>
            <h3 className="text-sm font-bold text-[#24152F]">
              Metas de Performance e Rendimento Físico
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Peso/Composição meta (kg)"
              isEditing={isEditing}
              readOnlyValue={profile.goalWeight}
              readOnlySuffix={profile.goalWeight ? " kg" : ""}
            >
              <Input
                type="number"
                placeholder="Ex: 75"
                value={profile.goalWeight}
                onChange={(event) =>
                  setProfile((prev) => ({
                    ...prev,
                    goalWeight: event.target.value,
                  }))
                }
              />
            </FormField>

            <FormField
              label="Data limite da meta de performance"
              isEditing={isEditing}
              readOnlyValue={formatDateBR(profile.goalDate)}
            >
              <Input
                type="date"
                value={profile.goalDate}
                onChange={(event) =>
                  setProfile((prev) => ({
                    ...prev,
                    goalDate: event.target.value,
                  }))
                }
              />
            </FormField>
          </div>

          <div className="rounded-2xl bg-[#ECE7FA]/50 p-3.5 border border-[#ECE7FA] text-xs space-y-1 text-[#4B367C]">
            <p className="font-bold">💪 Foco do seu programa:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs text-[#6D6680] font-medium">
              <li>Ganho de força, hipertrofia muscular e melhora de rendimento esportivo.</li>
              <li>Otimização da suplementação, ingestão proteica e recuperação física.</li>
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}